import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import AttributeForm from '../../components/Configuration/AttributeForm';
import { getAttributes, deleteAttribute } from '../../services/attributesService';

const Attributes = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const [allAttributes, setAllAttributes] = useState([]);

  // Cargar atributos desde el backend (solo una vez)
  const loadAttributes = async () => {
    try {
      setLoading(true);
      console.log('🔄 [Attributes] Iniciando carga de atributos...');
      
      const response = await getAttributes();
      
      console.log('📥 [Attributes] Respuesta recibida en componente:');
      console.log('📦 [Attributes] response:', response);
      console.log('✅ [Attributes] response.success:', response.success);
      console.log('📋 [Attributes] response.data:', response.data);
      console.log('🔍 [Attributes] Tipo de response.data:', typeof response.data);
      console.log('📊 [Attributes] Es array?:', Array.isArray(response.data));
      
      if (response.success) {
        const attributesData = response.data || [];
        console.log('💾 [Attributes] Guardando atributos en estado:');
        console.log('🔢 [Attributes] Cantidad de atributos a guardar:', attributesData.length);
        console.log('📝 [Attributes] Primeros 3 atributos:', attributesData.slice(0, 3));
        
        if (attributesData.length > 0) {
          console.log('🔑 [Attributes] Estructura del primer atributo:', {
            id: attributesData[0].id,
            code: attributesData[0].code,
            name: attributesData[0].name,
            type: attributesData[0].type,
            is_required: attributesData[0].is_required,
            is_active: attributesData[0].is_active,
            options: attributesData[0].options,
            keys: Object.keys(attributesData[0])
          });
        }
        
        setAllAttributes(attributesData);
        console.log('✅ [Attributes] Atributos guardados en estado exitosamente');
      } else {
        console.warn('⚠️ [Attributes] Respuesta sin éxito:', response);
        setToast({
          isOpen: true,
          message: 'Error al cargar los atributos',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Attributes] Error al cargar atributos:', error);
      console.error('📊 [Attributes] Error.response:', error.response);
      console.error('📋 [Attributes] Error.response?.data:', error.response?.data);
      console.error('🔢 [Attributes] Error.status:', error.response?.status);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar los atributos',
        type: 'error'
      });
    } finally {
      setLoading(false);
      console.log('🏁 [Attributes] Proceso de carga finalizado');
    }
  };

  useEffect(() => {
    // Evitar doble carga en React StrictMode - Carga inicial única
    if (!hasLoaded.current) {
      console.log('🚀 [Attributes] Componente montado, iniciando carga de datos...');
      console.log('🔍 [Attributes] hasLoaded.current:', hasLoaded.current);
      hasLoaded.current = true;
      loadAttributes();
    } else {
      console.log('⏭️ [Attributes] Ya se cargaron los datos anteriormente, omitiendo carga');
    }
  }, []);

  // Filtrar atributos localmente en el frontend
  const filteredAttributes = allAttributes.filter(attribute => {
    const matchesSearch = !searchTerm || 
      attribute.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attribute.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = typeFilter === 'all' || attribute.type === typeFilter;
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && attribute.is_active) ||
      (statusFilter === 'inactive' && !attribute.is_active);

    return matchesSearch && matchesType && matchesStatus;
  });

  // Calcular estadísticas
  const totalAttributes = filteredAttributes.length;
  const activeAttributes = filteredAttributes.filter(a => a.is_active).length;
  const requiredAttributes = filteredAttributes.filter(a => a.is_required).length;

  const handleDeleteClick = (attribute) => {
    setAttributeToDelete(attribute);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!attributeToDelete) return;
    
    try {
      const response = await deleteAttribute(attributeToDelete.id);
      
      if (response.success) {
        setAllAttributes(prev => prev.filter(a => a.id !== attributeToDelete.id));
        setToast({
          isOpen: true,
          message: 'Atributo eliminado exitosamente',
          type: 'success'
        });
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar el atributo',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar atributo:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar el atributo',
        type: 'error'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setAttributeToDelete(null);
    }
  };

  const handleFormSuccess = () => {
    // Recargar todos los atributos desde el backend
    loadAttributes();
    setToast({
      isOpen: true,
      message: editingAttribute ? 'Atributo actualizado exitosamente' : 'Atributo creado exitosamente',
      type: 'success'
    });
  };

  const getTypeLabel = (type) => {
    const types = {
      text: 'Texto',
      number: 'Número',
      select: 'Selección',
      boolean: 'Booleano',
      date: 'Fecha'
    };
    return types[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      text: 'bg-blue-100 text-blue-800',
      number: 'bg-purple-100 text-purple-800',
      select: 'bg-green-100 text-green-800',
      boolean: 'bg-yellow-100 text-yellow-800',
      date: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Atributos de Productos</h1>
              <p className="text-gray-600 mt-1">Gestione los atributos personalizados para productos</p>
            </div>
            <button
              onClick={() => {
                setEditingAttribute(null);
                setIsFormOpen(true);
              }}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
            >
              + Nuevo Atributo
            </button>
          </div>

          {/* Breadcrumb */}
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/configuration" className="hover:text-teal-600">Configuración</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Atributos</span>
          </nav>

          {/* Filtros */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex gap-2">
              {/* Filtro por Tipo */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">Todos los Tipos</option>
                <option value="text">Texto</option>
                <option value="number">Número</option>
                <option value="select">Selección</option>
                <option value="boolean">Booleano</option>
                <option value="date">Fecha</option>
              </select>

              {/* Filtro por Estado */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">Todos los Estados</option>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
              </select>

              {/* Búsqueda */}
              <input
                type="text"
                placeholder="Buscar por código o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={() => {
                  setSearchTerm('');
                  setTypeFilter('all');
                  setStatusFilter('all');
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Total de Atributos</div>
            <div className="text-2xl font-bold text-gray-800">{totalAttributes}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Atributos Activos</div>
            <div className="text-2xl font-bold text-green-600">{activeAttributes}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Atributos Obligatorios</div>
            <div className="text-2xl font-bold text-blue-600">{requiredAttributes}</div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              Cargando atributos...
            </div>
          ) : filteredAttributes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
                ? 'No se encontraron atributos con los filtros aplicados' 
                : 'No hay atributos registrados'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requerido
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Opciones
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAttributes.map((attribute) => (
                    <tr key={attribute.id} className="hover:bg-gray-50">
                      {/* Código */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono font-medium text-teal-600">{attribute.code}</span>
                      </td>

                      {/* Nombre */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{attribute.name}</span>
                      </td>

                      {/* Tipo */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(attribute.type)}`}>
                          {getTypeLabel(attribute.type)}
                        </span>
                      </td>

                      {/* Requerido */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          attribute.is_required
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {attribute.is_required ? 'Sí' : 'No'}
                        </span>
                      </td>

                      {/* Opciones */}
                      <td className="px-6 py-4">
                        {attribute.type === 'select' && attribute.options ? (
                          <div className="text-sm text-gray-600">
                            {attribute.options.length} opciones
                            <div className="text-xs text-gray-500 mt-1">
                              {attribute.options.slice(0, 3).join(', ')}
                              {attribute.options.length > 3 && '...'}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>

                      {/* Estado */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          attribute.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {attribute.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>

                      {/* Acciones */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setEditingAttribute(attribute);
                            setIsFormOpen(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 mr-4"
                          title="Editar"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteClick(attribute)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de Formulario */}
        <AttributeForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingAttribute(null);
          }}
          attribute={editingAttribute}
          onSuccess={handleFormSuccess}
        />

        {/* Modal de Confirmación de Eliminación */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setAttributeToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Atributo"
          message={`¿Estás seguro de que deseas eliminar el atributo "${attributeToDelete?.name}" (${attributeToDelete?.code})? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default Attributes;

